import {useEffect, useRef, useState} from 'react';

export type SourceScriptControlTarget = {
  sceneKey: string;
  signature: string;
  selectionRevision: number;
};
export type SourceScriptCatalogOption = {id: string; label: string};
export const sourceScriptFieldClass = 'source-field';
export const sourceScriptButtonClass = 'source-button';

/** A dirty field retains its original document/scene, including across IME events. */
export function SourceScriptDraftField({label, value, identity, getTarget, onSave, onComposition, disabled = false, multiline = false, type = 'text', min, max, step, saveLabel, allowPristine = false, choices, hideLabel = false}: {
  label: string;
  value: string;
  identity: string;
  getTarget: () => SourceScriptControlTarget;
  onSave: (value: string, target: SourceScriptControlTarget) => Promise<boolean>;
  onComposition: (identity: string, active: boolean) => void;
  disabled?: boolean;
  multiline?: boolean;
  type?: 'text' | 'number';
  min?: number;
  max?: number;
  step?: number;
  saveLabel?: string;
  allowPristine?: boolean;
  choices?: readonly SourceScriptCatalogOption[];
  hideLabel?: boolean;
}) {
  const [draft, setDraft] = useState(value);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const draftRef = useRef(value);
  const dirtyRef = useRef(false);
  const savingRef = useRef(false);
  const composing = useRef(false);
  const captured = useRef<SourceScriptControlTarget | null>(null);
  const latest = useRef({value, getTarget, onComposition});
  latest.current = {value, getTarget, onComposition};
  useEffect(() => {if (!dirty && !composing.current) {draftRef.current = value; setDraft(value);}}, [value, identity, dirty]);
  useEffect(() => () => latest.current.onComposition(identity, false), [identity]);
  const capture = () => {captured.current ??= getTarget();};
  const change = (text: string) => {capture(); dirtyRef.current = true; draftRef.current = text; setDirty(true); setDraft(text);};
  async function save() {
    if (composing.current || disabled || savingRef.current || (!dirtyRef.current && !allowPristine)) return;
    captured.current ??= getTarget();
    savingRef.current = true; setSaving(true);
    try {
      if (await onSave(draftRef.current, captured.current)) {captured.current = null; dirtyRef.current = false; setDirty(false);}
    } finally {savingRef.current = false; setSaving(false);}
  }
  const shared = {
    className: sourceScriptFieldClass, 'aria-label': label, value: draft, disabled: disabled || saving,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => change(e.currentTarget.value),
    onCompositionStart: () => {capture(); composing.current = true; onComposition(identity, true);},
    onCompositionEnd: (e: React.CompositionEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      change(e.currentTarget.value); composing.current = false; onComposition(identity, false);
    },
    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !composing.current && !e.nativeEvent.isComposing && e.keyCode !== 229) {
        e.preventDefault(); void save();
      }
    },
  };
  return <div className="grid gap-2xs">
    <label className="grid gap-1xs"><span className={hideLabel ? 'source-sr-only' : undefined}>{label}</span>{choices ? <select {...shared}>{choices.map(choice => <option value={choice.id} key={choice.id}>{choice.label}</option>)}</select> : multiline ? <textarea {...shared} rows={3}/> : <input {...shared} type={type} min={min} max={max} step={step}/>}</label>
    <div className="flex flex-wrap gap-2xs">
      <button className={`${sourceScriptButtonClass} source-button-primary`} type="button" disabled={disabled || saving || (!dirty && !allowPristine)} onClick={() => void save()}>{saveLabel ?? `${label} 적용`}</button>
      {dirty && <button className={sourceScriptButtonClass} type="button" disabled={saving} onClick={() => {
        if (composing.current) return;
        captured.current = null; dirtyRef.current = false; draftRef.current = latest.current.value; setDirty(false); setDraft(latest.current.value);
      }}>{label} 다시 불러오기</button>}
    </div>
  </div>;
}
